import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Title,
  Text,
  Group,
  Button,
  Stack,
  TextInput,
  NumberInput,
  ActionIcon,
  Badge,
  Table,
  Alert,
  FileButton,
  List,
  Anchor,
} from "@mantine/core";
import { IconArrowLeft, IconCheck, IconUpload, IconTrash, IconFile } from "@tabler/icons-react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { rfqApi } from "../../../api/rfq";
import { quotationsApi } from "../../../api/quotations";
import { notifications } from "@mantine/notifications";
import dayjs from "dayjs";

export function VendorQuotationForm() {
  const navigate = useNavigate();
  const { rfqId } = useParams<{ rfqId: string }>();
  const queryClient = useQueryClient();

  const { data: rfq, isLoading: rfqLoading } = useQuery({
    queryKey: ["rfq", rfqId, "vendor"],
    queryFn: () => rfqApi.getVendorRFQ(rfqId!),
    enabled: !!rfqId,
  });

  const { data: existingQuotation, refetch: refetchQuotation } = useQuery({
    queryKey: ["quotation", rfqId],
    queryFn: async () => {
      if (!rfqId) return null;
      try {
        const quotations = await quotationsApi.list(rfqId);
        // Find any quotation for current vendor (DRAFT first, then SUBMITTED, then others)
        const draft = quotations.find((q) => q.status === "DRAFT");
        if (draft) return draft;
        const submitted = quotations.find((q) => q.status === "SUBMITTED");
        if (submitted) return submitted;
        return quotations[0] || null;
      } catch (error) {
        console.error("Error fetching quotations:", error);
        return null;
      }
    },
    enabled: !!rfqId,
  });

  const isReadOnly = existingQuotation?.status === "SUBMITTED" || existingQuotation?.status === "ACCEPTED" || existingQuotation?.status === "REJECTED";

  const [formData, setFormData] = useState({
    validityDays: 30,
    deliveryDays: 0,
    paymentTerms: "",
    notes: "",
  });

  const [items, setItems] = useState<
    Array<{
      rfqItemId: string;
      quantity: number;
      unit: string;
      unitPrice: number;
      totalPrice: number;
      description?: string;
    }>
  >([]);

  const [documents, setDocuments] = useState<Array<{ id: string; fileName: string; fileSize: number; uploadedAt: string }>>([]);

  useEffect(() => {
    if (rfq && rfq.items) {
      setItems(
        rfq.items.map((item) => ({
          rfqItemId: item.id,
          quantity: item.quantity,
          unit: item.unit,
          unitPrice: 0,
          totalPrice: 0,
          description: item.description,
        }))
      );
    }
  }, [rfq]);

  useEffect(() => {
    if (existingQuotation) {
      setFormData({
        validityDays: existingQuotation.validityDays,
        deliveryDays: existingQuotation.deliveryDays,
        paymentTerms: existingQuotation.paymentTerms || "",
        notes: existingQuotation.notes || "",
      });
      // Only update items if they exist and are different from current items
      // This prevents overwriting user input when refetching
      if (existingQuotation.items && existingQuotation.items.length > 0) {
        setItems(
          existingQuotation.items.map((item) => ({
            rfqItemId: item.rfqItemId,
            quantity: item.quantity,
            unit: item.unit,
            unitPrice: Number(item.unitPrice) || 0,
            totalPrice: Number(item.totalPrice) || 0,
            description: item.description,
          }))
        );
      }
    }
  }, [existingQuotation]);

  // Load documents when quotation exists
  const { data: quotationDocuments, refetch: refetchDocuments } = useQuery({
    queryKey: ["quotation-documents", existingQuotation?.id],
    queryFn: () => quotationsApi.listDocuments(existingQuotation!.id),
    enabled: !!existingQuotation?.id,
  });

  useEffect(() => {
    if (quotationDocuments) {
      setDocuments(quotationDocuments);
    }
  }, [quotationDocuments]);

  const updateItem = (
    rfqItemId: string,
    field: string,
    value: number | string
  ) => {
    setItems(
      items.map((item) => {
        if (item.rfqItemId === rfqItemId) {
          const updated = { ...item, [field]: value };
          if (field === "unitPrice" || field === "quantity") {
            updated.totalPrice =
              Number(updated.quantity) * Number(updated.unitPrice);
          }
          return updated;
        }
        return item;
      })
    );
  };

  const createQuotation = useMutation({
    mutationFn: (data: any) => quotationsApi.create(rfqId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["quotation", rfqId] });
      queryClient.invalidateQueries({ queryKey: ["vendor-rfqs"] });
      notifications.show({
        title: "Success",
        message: "Quotation draft saved successfully",
        color: "green",
      });
      // Refetch the quotation to update the form with saved data
      refetchQuotation();
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.response?.data?.error || "Failed to create quotation",
        color: "red",
      });
    },
  });

  const updateQuotation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      quotationsApi.update(rfqId!, id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["quotation", rfqId] });
      queryClient.invalidateQueries({ queryKey: ["vendor-rfqs"] });
      notifications.show({
        title: "Success",
        message: "Quotation draft saved successfully",
        color: "green",
      });
      // Refetch the quotation to update the form with saved data
      refetchQuotation();
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.response?.data?.error || "Failed to update quotation",
        color: "red",
      });
    },
  });

  const submitQuotation = useMutation({
    mutationFn: (id: string) => quotationsApi.submit(rfqId!, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["quotation", rfqId] });
      queryClient.invalidateQueries({ queryKey: ["vendor-rfqs"] });
      notifications.show({
        title: "Success",
        message: "Quotation submitted successfully",
        color: "green",
      });
      navigate(`/vendor/rfq/${rfqId}`);
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.response?.data?.error || "Failed to submit quotation",
        color: "red",
      });
    },
  });

  const uploadDocument = useMutation({
    mutationFn: ({ quotationId, file }: { quotationId: string; file: File }) =>
      quotationsApi.uploadDocument(quotationId, file, "QUOTATION"),
    onSuccess: () => {
      refetchDocuments();
      notifications.show({
        title: "Success",
        message: "Document uploaded successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.response?.data?.error || "Failed to upload document",
        color: "red",
      });
    },
  });

  const deleteDocument = useMutation({
    mutationFn: ({ quotationId, documentId }: { quotationId: string; documentId: string }) =>
      quotationsApi.deleteDocument(quotationId, documentId),
    onSuccess: () => {
      refetchDocuments();
      notifications.show({
        title: "Success",
        message: "Document deleted successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.response?.data?.error || "Failed to delete document",
        color: "red",
      });
    },
  });

  const handleFileUpload = (file: File | null) => {
    if (!file || !existingQuotation) return;

    if (file.size > 10 * 1024 * 1024) {
      notifications.show({
        title: "Error",
        message: "File size must be less than 10MB",
        color: "red",
      });
      return;
    }

    uploadDocument.mutate({ quotationId: existingQuotation.id, file });
  };

  const handleDeleteDocument = (documentId: string) => {
    if (!existingQuotation) return;
    deleteDocument.mutate({ quotationId: existingQuotation.id, documentId });
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + " KB";
    return (bytes / (1024 * 1024)).toFixed(2) + " MB";
  };

  const handleSave = () => {
    // Ensure all numeric values are properly converted to numbers
    const quotationData = {
      validityDays: Number(formData.validityDays) || 30,
      deliveryDays: Number(formData.deliveryDays) || 0,
      paymentTerms: formData.paymentTerms || "",
      notes: formData.notes || "",
      items: items.map((item) => ({
        rfqItemId: item.rfqItemId,
        quantity: Number(item.quantity) || 1,
        unit: item.unit || "unit",
        unitPrice: Number(item.unitPrice) || 0,
        totalPrice: Number(item.totalPrice) || 0,
        description: item.description || "",
      })),
    };

    console.log("Saving quotation with items:", quotationData.items); // Debug log

    if (existingQuotation) {
      updateQuotation.mutate({
        id: existingQuotation.id,
        data: quotationData,
      });
    } else {
      createQuotation.mutate(quotationData);
    }
  };

  const handleSubmit = () => {
    if (!existingQuotation) {
      notifications.show({
        title: "Error",
        message: "Please save the quotation first",
        color: "red",
      });
      return;
    }

    // Validate all items have prices
    const invalidItems = items.filter(
      (item) => !item.unitPrice || item.unitPrice <= 0
    );
    if (invalidItems.length > 0) {
      notifications.show({
        title: "Validation Error",
        message: "Please provide prices for all items",
        color: "red",
      });
      return;
    }

    submitQuotation.mutate(existingQuotation.id);
  };

  if (rfqLoading) {
    return (
      <Container size="xl" py="xl">
        <Text>Loading RFQ...</Text>
      </Container>
    );
  }

  if (!rfq) {
    return (
      <Container size="xl" py="xl">
        <Alert color="red">RFQ not found</Alert>
      </Container>
    );
  }

  const totalPrice = items.reduce((sum, item) => sum + item.totalPrice, 0);

  return (
    <Container size="xl" py="xl">
      <Group mb="xl">
        <ActionIcon
          variant="subtle"
          onClick={() => navigate(`/vendor/rfq/${rfqId}`)}
        >
          <IconArrowLeft size={20} />
        </ActionIcon>
        <Title order={2}>
          {isReadOnly ? "View Quotation" : "Submit Quotation"}
        </Title>
        {isReadOnly && existingQuotation && (
          <Badge
            color={
              existingQuotation.status === "ACCEPTED"
                ? "green"
                : existingQuotation.status === "SUBMITTED"
                ? "blue"
                : "gray"
            }
            variant="light"
          >
            {existingQuotation.status}
          </Badge>
        )}
      </Group>

      <Stack gap="md">
        <Paper shadow="sm" p="md" withBorder>
          <Group justify="space-between">
            <Stack gap="xs">
              <Text fw={500}>{rfq.title}</Text>
              <Text size="sm" c="dimmed">
                RFQ Number: {rfq.rfqNumber}
              </Text>
              <Text size="sm" c="dimmed">
                Deadline: {dayjs(rfq.deadline).format("DD MMM YYYY HH:mm")}
              </Text>
              {existingQuotation?.submittedAt && (
                <Text size="sm" c="dimmed">
                  Submitted: {dayjs(existingQuotation.submittedAt).format("DD MMM YYYY HH:mm")}
                </Text>
              )}
            </Stack>
            <Badge color="red" variant="light">
              {dayjs(rfq.deadline).diff(dayjs(), "days")} days remaining
            </Badge>
          </Group>
        </Paper>

        <Paper shadow="sm" p="md">
          <Stack gap="md">
            <Group grow>
              <NumberInput
                label="Validity Days"
                placeholder="Enter validity in days"
                required
                min={1}
                value={formData.validityDays}
                onChange={(value) =>
                  setFormData({
                    ...formData,
                    validityDays: Number(value) || 30,
                  })
                }
                readOnly={isReadOnly}
                disabled={isReadOnly}
              />
              <NumberInput
                label="Delivery Days"
                placeholder="Enter delivery time in days"
                required
                min={0}
                value={formData.deliveryDays}
                onChange={(value) =>
                  setFormData({
                    ...formData,
                    deliveryDays: Number(value) || 0,
                  })
                }
                readOnly={isReadOnly}
                disabled={isReadOnly}
              />
            </Group>
            <TextInput
              label="Payment Terms"
              placeholder="Enter payment terms (optional)"
              value={formData.paymentTerms}
              onChange={(e) =>
                setFormData({ ...formData, paymentTerms: e.target.value })
              }
              readOnly={isReadOnly}
              disabled={isReadOnly}
            />
            <TextInput
              label="Notes"
              placeholder="Enter additional notes (optional)"
              value={formData.notes}
              onChange={(e) =>
                setFormData({ ...formData, notes: e.target.value })
              }
              readOnly={isReadOnly}
              disabled={isReadOnly}
            />
          </Stack>
        </Paper>

        <Paper shadow="sm" p="md">
          <Stack gap="md">
            <Title order={4}>Documents</Title>
            <Text size="sm" c="dimmed">
              Attach supporting documents (PDF, images, etc.) to your quotation. Maximum file size: 10MB.
            </Text>
            {existingQuotation ? (
              <>
                {!isReadOnly && (
                  <Group>
                    <FileButton
                      onChange={handleFileUpload}
                      accept="application/pdf,image/*,.doc,.docx"
                      disabled={uploadDocument.isPending}
                    >
                      {(props) => (
                        <Button
                          {...props}
                          leftSection={<IconUpload size={16} />}
                          loading={uploadDocument.isPending}
                        >
                          Upload Document
                        </Button>
                      )}
                    </FileButton>
                  </Group>
                )}
                {documents.length > 0 && (
                  <List>
                    {documents.map((doc) => (
                      <List.Item
                        key={doc.id}
                        icon={<IconFile size={16} />}
                      >
                        <Group justify="space-between">
                          <Anchor
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              // TODO: Implement document download
                            }}
                          >
                            {doc.fileName}
                          </Anchor>
                          <Group gap="xs">
                            <Text size="xs" c="dimmed">
                              {formatFileSize(doc.fileSize)}
                            </Text>
                            {!isReadOnly && (
                              <ActionIcon
                                color="red"
                                variant="subtle"
                                size="sm"
                                onClick={() => handleDeleteDocument(doc.id)}
                                loading={deleteDocument.isPending}
                              >
                                <IconTrash size={14} />
                              </ActionIcon>
                            )}
                          </Group>
                        </Group>
                      </List.Item>
                    ))}
                  </List>
                )}
                {documents.length === 0 && (
                  <Text size="sm" c="dimmed" fs="italic">
                    No documents uploaded yet
                  </Text>
                )}
              </>
            ) : (
              <Alert color="blue">
                Please save the quotation first before uploading documents.
              </Alert>
            )}
          </Stack>
        </Paper>

        <Paper shadow="sm" p="md">
          <Title order={4} mb="md">
            Items & Pricing
          </Title>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Item Name</Table.Th>
                <Table.Th>Quantity</Table.Th>
                <Table.Th>Unit</Table.Th>
                <Table.Th>Unit Price</Table.Th>
                <Table.Th>Total Price</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {items.map((item) => {
                const rfqItem = rfq.items.find((i) => i.id === item.rfqItemId);
                return (
                  <Table.Tr key={item.rfqItemId}>
                    <Table.Td>{rfqItem?.itemName}</Table.Td>
                    <Table.Td>{item.quantity}</Table.Td>
                    <Table.Td>{item.unit}</Table.Td>
                    <Table.Td>
                      {isReadOnly ? (
                        <Text fw={500}>
                          {rfq.currency} {item.unitPrice.toFixed(2)}
                        </Text>
                      ) : (
                        <NumberInput
                          placeholder="Enter unit price"
                          required
                          min={0}
                          decimalScale={2}
                          value={item.unitPrice}
                          onChange={(value) =>
                            updateItem(item.rfqItemId, "unitPrice", Number(value) || 0)
                          }
                          style={{ width: 150 }}
                          readOnly={isReadOnly}
                          disabled={isReadOnly}
                        />
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Text fw={500}>
                        {rfq.currency} {item.totalPrice.toFixed(2)}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
          <Group justify="flex-end" mt="md">
            <Text fw={600} size="lg">
              Total: {rfq.currency} {totalPrice.toFixed(2)}
            </Text>
          </Group>
        </Paper>

        <Group justify="flex-end">
          <Button variant="subtle" onClick={() => navigate(`/vendor/rfq/${rfqId}`)}>
            {isReadOnly ? "Back" : "Cancel"}
          </Button>
          {!isReadOnly && (
            <>
              <Button
                onClick={handleSave}
                loading={createQuotation.isPending || updateQuotation.isPending}
              >
                Save Draft
              </Button>
              <Button
                color="green"
                leftSection={<IconCheck size={16} />}
                onClick={handleSubmit}
                loading={submitQuotation.isPending}
                disabled={!existingQuotation}
              >
                Submit Quotation
              </Button>
            </>
          )}
        </Group>
      </Stack>
    </Container>
  );
}

